import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import {
  AuthManagerService,
  AuthParams,
  UserAuthenticationType,
} from 'src/app/services/auth-manager/auth-manager.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ErrorStrings } from 'src/app/constants/form.constants';
import { Alert } from 'src/app/interfaces/alert';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
  RedirectionParams,
  RedirectionService,
} from 'src/app/services/redirection/redirection.service';
import { ACCOUNT_TYPE_PREVIEW_ROUTE } from 'src/app/constants/route.constants';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { UserService } from 'src/app/services/user/user.service';
import { ErrorCode, User } from 'src/app/interfaces/user';
import { RegistrationProgressService } from 'src/app/services/registration-progress/registration-progress.service';
import { isPlatformBrowser } from '@angular/common';
import { ConfigService } from 'src/app/services/config/config.service';
import { of, switchMap } from 'rxjs';
import { LoginNavigationService } from 'src/app/services/login-navigation/login-navigation.service';
import { RegistrationFlowService } from 'src/app/services/registration-flow/registration-flow.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { errorMessagesMap } from 'src/app/constants/error-messages';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  loading = false;
  alert: Alert = null;
  errorStrings = ErrorStrings;
  adminLoginUrl = environment.adminLoginUrl;
  redirectTimer = 0;
  redirectParams?: RedirectionParams;
  hidePassword: boolean = true;
  authWithChipNumber: boolean = false;
  isBrowser: boolean;
  companyPhoneNumber: string;
  useRebrandedLayout: boolean;

  model: AuthParams = {
    identifier: '',
    password: '',
    authenticationType: UserAuthenticationType.Email,
  };

  constructor(
    public authManager: AuthManagerService,
    private router: Router,
    private route: ActivatedRoute,
    private redirectionService: RedirectionService,
    private registrationProgressService: RegistrationProgressService,
    private loginNavigationService: LoginNavigationService,
    private registrationFlowService: RegistrationFlowService,
    private configService: ConfigService,
    private featureFlagService: FeatureFlagsService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.companyPhoneNumber = this.configService.getTextFromKey('company-phone-number');
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
  }

  public onAuthWithChipNumberChanged(event: MatSlideToggleChange) {
    this.authWithChipNumber = event.checked;
    this.model.identifier = '';
  }

  ngOnInit(): void {
    if (this.authManager.isAuthenticated) {
      // The check after login reuses the cached steps, which can be stale this late in the session
      this.registrationFlowService.clear();
      this.loginNavigationService.checkRegistrationProgressOnNextNavigation = true;
      this.router.navigate(['/dashboard']);
    }

    // Prefill email address if "email" is passed as query param
    // (this is set when a redirection from the signup page - 409 status code)
    if (this.route.snapshot.queryParams.email) {
      this.model.identifier = this.route.snapshot.queryParams.email;
      this.alert = {
        type: 'success',
        message: `It looks like you're already registered. Please enter your password to log in.`,
        bold: true,
      };
    }

    if (this.route.snapshot.queryParams.chip) {
      this.model.identifier = this.route.snapshot.queryParams.chip;
      this.authWithChipNumber = true;
      this.alert = {
        type: 'success',
        message: `It looks like chip number ${this.route.snapshot.queryParams.chip} is already registered. Please enter your password to log in.`,
        bold: true,
      };
    }

    // Set by the update password page, which ends the session
    if (this.route.snapshot.queryParams.passwordUpdated) {
      this.alert = {
        type: 'success',
        message: errorMessagesMap.password_updated_success,
        bold: true,
      };
    }

    // Setting redirection to be used when after login we want the user redirected to a specific page
    this.redirectParams = this.redirectionService.getParams(
      this.route.snapshot.queryParams.r
    );
  }

  login() {
    this.closeAlert();
    this.loading = true;
    this.trackEvent('login::clicked');

    this.model.authenticationType = this.authWithChipNumber
      ? UserAuthenticationType.ChipNumber
      : this.model.identifier.includes('@')
      ? UserAuthenticationType.Email
      : UserAuthenticationType.PhoneNumber;

    this.authManager.login(this.model).subscribe({
      next: (authToken) => {
        if (!authToken) {
          this.redirectToAdmin();
          return;
        }
        if (
          this.redirectParams?.l &&
          !this.redirectParams.l.includes(`/${ACCOUNT_TYPE_PREVIEW_ROUTE}`)
        ) {
          this.router.navigate(this.redirectParams.l, {
            queryParams: this.redirectParams.q,
            fragment: this.redirectParams.f,
          });
        } else {
          this.loginNavigationService.checkRegistrationProgressOnNextNavigation = true;
          this.router.navigate(['/dashboard']);
        }
      },
      error: (err) => {
        if (err instanceof HttpErrorResponse && err.error.status === 400) {
          if (err.error && err.error.errorCode === ErrorCode.MultiplePhoneNumbers) {
            this.alert = {
              type: 'danger',
              message: err.error.message,
            };

          } else if (err.error && err.error.errorCode === ErrorCode.UnknownFailure) {
            this.alert = {
              type: 'danger',
              message: `Please contact identi on ${this.companyPhoneNumber}.`,
            };
          }
          else {
            this.alert = {
              type: 'danger',
              message: `Please contact identi on ${this.companyPhoneNumber}.`,
            };
          }
        } else if (err instanceof HttpErrorResponse && err.status === 401) {
          if (err.error && err.error.errorCode === ErrorCode.InvalidCredentials) {
            this.alert = {
              type: 'danger',
              message: errorMessagesMap.login_error_message,
            };
          } else if (err.error && err.error.errorCode === ErrorCode.InvalidRole) {
            // Admin users trying to login exception
            this.redirectToAdmin();
          } 
          else {
            this.alert = {
              type: 'danger',
              message: errorMessagesMap.generic_error_message,
            };
          }
        } else if (err instanceof HttpErrorResponse && err.status === 423) {
          // Account temporarily locked after too many failed attempts
          this.alert = {
            type: 'danger',
            message: err.error?.message || errorMessagesMap.generic_error_message,
          };
        } else {
          this.alert = {
            type: 'danger',
            message: 'An error has occurred. Please try again later.',
          };
        }

        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    })
  }

  closeAlert() {
    this.alert = null;
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  private redirectToAdmin(): void {
    this.redirectTimer = 3;
    this.showRedirectToAdminMessage();
    if (this.isBrowser) {
      const timer = window.setInterval(() => {
        this.redirectTimer--;
        this.showRedirectToAdminMessage();
        if (!this.redirectTimer) {
          window.clearInterval(timer);
          window.location.href = this.adminLoginUrl;
        }
      }, 1000);
    }
  }

  private showRedirectToAdminMessage(): void {
    this.alert = {
      type: 'danger',
      message: `It looks like you're not a keeper. You will be redirected automatically in <strong>${this.redirectTimer}s</strong>. If you don't want to wait, please <a href="${this.adminLoginUrl}" rel="nofollow">log in here</a>.`,
    };
  }
}
